import { useEffect, useMemo, useState } from "react";





type TextVariable = {
  String_key: string,
  de?: string | null,
  en?: string | null,
  de_custom?: string | null,
  en_custom?: string | null,
};

type SortColumn = "String_key" | "de" | "en" | "de_custom" | "en_custom";
type SortDirection = "asc" | "desc";



type Props = {
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  eventId: string | null;
  onError: (message: string) => void;

};




export function TextVariables({ apiFetch, eventId, onError }: Props) {

  const [variables, SetVariables] = useState<TextVariable[]>([]);
  const [sortColumn, setSortColumn] = useState<SortColumn>("String_key");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortColumn(column);
    setSortDirection("asc");
  };

  const getSortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return "↕";
    return sortDirection === "asc" ? "▲" : "▼";
  };

  const sortedVariables = useMemo(() => {
    const sorted = [...variables];
    const directionMultiplier = sortDirection === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
      const aValue = a[sortColumn] ?? "";
      const bValue = b[sortColumn] ?? "";

      return String(aValue).localeCompare(String(bValue)) * directionMultiplier;
    });

    return sorted;
  }, [variables, sortColumn, sortDirection]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {

        var res = await apiFetch(`/api/EventPool/GetTextVariables?eventId=${eventId ?? ""}`)


        if (!active) return;

        if (res.ok) {
          const typesJson = await res.json();
          if (active) SetVariables(typesJson);
        }


      } catch (err) {
        console.error(err);
        onError("Failed to load user management settings");
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [apiFetch, eventId, onError]);


  const UpdateVariable = async (key: string, field: "de_custom" | "en_custom", value: string) => {

    //send as form data
    const values = { [field]: value, Event_id: eventId };

    var res = await apiFetch(`/api/EventPool/PutTextVariable?key=${key}&values=${encodeURIComponent(JSON.stringify(values))}`, {
      method: 'PUT'
    });

    if (!res.ok) {
      onError("Failed to update variable");
      return;
    }
   
  }



  return (
    <div className="mt-2 space-y-4">




      <div className="rounded-md border !mt-10">
        <div className="border-b bg-slate-100 px-3 py-2 text-sm font-bold text-gray-600">Override Text for this event:</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th
                  className="px-3 py-2 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-800"
                  onClick={() => handleSort("String_key")}
                >
                  <span className="inline-flex items-center gap-1">
                    Key
                    <span className="text-xs leading-none">{getSortIndicator("String_key")}</span>
                  </span>
                </th>
                <th
                  className="px-3 py-2 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-800"
                  onClick={() => handleSort("de")}
                >
                  <span className="inline-flex items-center gap-1">
                    DE (Original)
                    <span className="text-xs leading-none">{getSortIndicator("de")}</span>
                  </span>
                </th>
                <th
                  className="px-3 py-2 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-800"
                  onClick={() => handleSort("en")}
                >
                  <span className="inline-flex items-center gap-1">
                    EN (Original)
                    <span className="text-xs leading-none">{getSortIndicator("en")}</span>
                  </span>
                </th>
                <th
                  className="px-3 py-2 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-800"
                  onClick={() => handleSort("de_custom")}
                >
                  <span className="inline-flex items-center gap-1">
                    DE (Override)
                    <span className="text-xs leading-none">{getSortIndicator("de_custom")}</span>
                  </span>
                </th>
                <th
                  className="px-3 py-2 font-medium text-gray-600 cursor-pointer select-none hover:text-gray-800"
                  onClick={() => handleSort("en_custom")}
                >
                  <span className="inline-flex items-center gap-1">
                    EN (Override)
                    <span className="text-xs leading-none">{getSortIndicator("en_custom")}</span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedVariables?.length === 0 ? (
                <tr className="border-t">
                  <td colSpan={6} className="px-3 py-4 text-center text-gray-500">
                    No variables found
                  </td>
                </tr>
              ) : (
                sortedVariables?.map((item) => (
                  <tr key={item.String_key} className="border-t align-middle cursor-pointer hover:bg-slate-50" >
                    <td className="px-3 py-2">{item.String_key}</td>
                    <td className="px-3 py-2 max-w-[400px]">{item.de}</td>
                    <td className="px-3 py-2 max-w-[400px]">{item.en}</td>
                    <td className="px-3 py-2">

                      <input
                        type={"text"}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={item.de_custom ?? ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          SetVariables((prev) =>  
                            prev.map((v) =>
                              v.String_key === item.String_key ? { ...v, de_custom: newValue } : v
                            )
                          );
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          UpdateVariable(item.String_key, "de_custom", newValue);
                        }}
                      />

                    </td>
                    <td className="px-3 py-2">

                      <input
                        type={"text"}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                        value={item.en_custom ?? ""}
                         onChange={(e) => {
                          const newValue = e.target.value;
                          SetVariables((prev) =>  
                            prev.map((v) =>
                              v.String_key === item.String_key ? { ...v, en_custom: newValue } : v
                            )
                          );
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value;
                          UpdateVariable(item.String_key, "en_custom", newValue);
                        }}
                      />

                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>




    </div>
  );
}
